import { Autocomplete, TextField, Box, Typography, Tooltip, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface SimpleAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ id: string; name?: string; displayName?: string }>;
    error?: boolean;
    disabled?: boolean;
    placeholder?: string;
    sx?: SxProps<Theme>;
    'data-testid'?: string;
}

export const SimpleAutocomplete = ({
    value,
    onChange,
    options,
    error,
    disabled,
    placeholder = 'Required Field',
    sx,
    'data-testid': dataTestId
}: SimpleAutocompleteProps) => {
    const theme = useTheme();

    const selectedOption = options.find((opt) => opt.id === value) || null;

    return (
        <Autocomplete
            data-testid={dataTestId}
            value={selectedOption}
            onChange={(_, newValue) => {
                onChange(newValue?.id || '');
            }}
            options={options}
            disabled={disabled}
            getOptionLabel={(option) => option.displayName || option.name || ''}
            isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
            sx={{
                flexGrow: 1,
                height: '2rem',
                minHeight: '2rem',
                '& .MuiFormControl-root': { height: '2rem', minHeight: '2rem' },
                '& .MuiInputBase-root': {
                    height: '2rem',
                    minHeight: '2rem',
                    borderRadius: '8px'
                },
                ...sx
            }}
            ListboxProps={{
                style: { maxHeight: 300, overflowY: 'auto', paddingBlock: 0 }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="filled"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={error}
                    InputProps={{
                        ...params.InputProps,
                        hiddenLabel: true,
                        disableUnderline: true,
                        placeholder: error && !value ? placeholder : '',
                        sx: {
                            backgroundColor: `#f5f6f7 !important`,
                            color: '#000',
                            padding: '0 0.8rem !important',
                            height: '2rem',
                            minHeight: '2rem',
                            borderRadius: '8px',
                            outline: error ? 'solid 1px #EF5350' : 'none',
                            '&.Mui-focused': {
                                backgroundColor: '#EBEEFE !important'
                            },
                            '& .MuiInputBase-input': {
                                maxWidth: '55% !important',
                                overflow: 'hidden !important',
                                textOverflow: 'ellipsis !important',
                                whiteSpace: 'nowrap',
                                padding: '0 !important'
                            }
                        }
                    }}
                />
            )}
            renderOption={(props, option, { selected }) => {
                const label = option.displayName || option.name || '';
                return (
                    <Box
                        component="li"
                        {...props}
                        sx={{
                            border: 'solid 1px',
                            borderColor: theme.palette.primary[500],
                            height: '100%',
                            width: '100%',
                            color: selected ? theme.palette.primary.contrastText : '#54595E',
                            bgcolor: selected ? `${theme.palette.primary.dark} !important` : theme.palette.primary[300],
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: `${theme.palette.primary.dark} !important`,
                                color: theme.palette.primary.contrastText
                            }
                        }}
                    >
                        <Tooltip title={label} placement="top" arrow>
                            <Typography noWrap sx={{ flex: 1, px: 1 }}>
                                {label}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            }}
        />
    );
};
